import { useCallback } from 'react';
import {
  getExtension,
  getMimeFromExtension,
  validateFiles,
  type FileValidationError,
} from '../constants/fileUploadConstants';
import {
  deleteStoredFile,
  uploadFile,
  uploadFileBulk,
} from '../services/fileStorageApi';
import { useEsaAttachmentStore } from '../store/esaAttachmentStore';

// ---------------------------------------------------------------------------
// Hook return type
// ---------------------------------------------------------------------------

export interface UseFileUploadReturn {
  /** Whether the file attachment UI should be shown. */
  isAttachEnabled: boolean;
  /** Validate then upload each file directly via POST /upload/file. */
  handleFilesSelected: (files: FileList | File[]) => Promise<void>;
  /** Abort any in-flight upload, delete from FSS if ukey exists, remove from store. */
  handleRemove: (localId: string) => Promise<void>;
  /** Re-upload a failed file from scratch. */
  handleRetry: (localId: string) => Promise<void>;
  /** Validation errors from the last call to handleFilesSelected. */
  lastValidationErrors: FileValidationError[];
}

// ---------------------------------------------------------------------------
// useFileUpload hook
// ---------------------------------------------------------------------------

export function useFileUpload(): UseFileUploadReturn {
  const isAttachEnabled = true;

  const store = useEsaAttachmentStore();

  const handleFilesSelected = useCallback(
    async (incoming: FileList | File[]): Promise<void> => {
      const files = Array.from(incoming);
      const { valid } = validateFiles(files, store.attachments.length);
      if (valid.length === 0) return;

      // Step 1: Add all valid files to the store as 'pending' → 'creating'.
      const localIdMap: Array<{ localId: string; file: File }> = valid.map(file => ({
        localId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}-${file.name}`,
        file,
      }));

      useEsaAttachmentStore.getState().addAttachments(
        localIdMap.map(({ localId, file }) => ({
          localId,
          file,
          fileType: getMimeFromExtension(getExtension(file.name)) ?? 'application/octet-stream',
          fileExtension: getExtension(file.name),
        })),
      );
      useEsaAttachmentStore.getState().setCreating(localIdMap.map(m => m.localId));

      // Step 2: Upload files.
      // Single file → POST /upload/file; multiple files → POST /upload/bulk.
      if (localIdMap.length === 1) {
        const { localId, file } = localIdMap[0];
        try {
          const response = await uploadFile(file);
          const ukey = response.stored_file_ukey;
          useEsaAttachmentStore.getState().setUploading({ [localId]: ukey });
          useEsaAttachmentStore.getState().markReady(ukey);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          useEsaAttachmentStore.getState().markFailed(localId, msg);
        }
      } else {
        try {
          const responses = await uploadFileBulk(localIdMap.map(m => m.file));
          const localIdToUkey: Record<string, string> = {};
          for (let i = 0; i < localIdMap.length; i++) {
            const { localId } = localIdMap[i];
            const res = responses[i];
            if (res?.stored_file_ukey) {
              localIdToUkey[localId] = res.stored_file_ukey;
            } else {
              useEsaAttachmentStore.getState().markFailed(localId, 'Upload failed');
            }
          }
          if (Object.keys(localIdToUkey).length > 0) {
            useEsaAttachmentStore.getState().setUploading(localIdToUkey);
            for (const ukey of Object.values(localIdToUkey)) {
              useEsaAttachmentStore.getState().markReady(ukey);
            }
          }
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          for (const { localId } of localIdMap) {
            useEsaAttachmentStore.getState().markFailed(localId, msg);
          }
        }
      }
    },
    [store.attachments.length],
  );

  const handleRemove = useCallback(async (localId: string): Promise<void> => {
    const att = useEsaAttachmentStore.getState().getAttachment(localId);
    if (!att) return;

    // Abort XHR if an upload is in flight
    if (att.xhr) {
      att.xhr.abort();
    }

    // Only call DELETE if the file has a ukey (i.e., was created in FSS)
    if (att.ukey) {
      try {
        await deleteStoredFile(att.ukey);
      } catch {
        // Soft-delete failure is non-fatal — remove from store anyway
      }
    }

    useEsaAttachmentStore.getState().removeAttachment(localId);
  }, []);

  const handleRetry = useCallback(async (localId: string): Promise<void> => {
    const att = useEsaAttachmentStore.getState().getAttachment(localId);
    if (!att) return;

    useEsaAttachmentStore.getState().setCreating([localId]);
    try {
      const response = await uploadFile(att.file);
      const ukey = response.stored_file_ukey;
      useEsaAttachmentStore.getState().setUploading({ [localId]: ukey });
      useEsaAttachmentStore.getState().markReady(ukey);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      useEsaAttachmentStore.getState().markFailed(localId, msg);
    }
  }, []);

  return {
    isAttachEnabled,
    handleFilesSelected,
    handleRemove,
    handleRetry,
    lastValidationErrors: [],
  };
}
